import {
  isWriteRefusal,
  isWriteRefusedBody,
  rejectedControlNames,
  writeRefusedMessage,
} from '@ups/xplat/utils';
import type {
  IDynamicModel,
  IDynamicModelGroup,
  IDynamicResponseBody,
} from '../models';
import type { DynamicRenderService } from '../services/dynamic-render.service';
import { dynamicFindControlByCondition } from './dynamic-helpers';

/** After a saved NEW form, resets its control state and clears it; a loaded response is left as is (WI 32220: scoped to this form). */
export function dynamicClearNewFormAfterSave(
  dynamicRender: Pick<
    DynamicRenderService,
    | 'activeFormResponse'
    | 'activeForm'
    | 'resetControlState'
    | 'clearActiveForm'
  >,
  group: Pick<IDynamicModelGroup, 'controls'>
): void {
  if (!dynamicRender.activeFormResponse) {
    dynamicRender.resetControlState(group.controls, dynamicRender.activeForm);
    dynamicRender.clearActiveForm();
  }
}

/** Shown when saveDynamicResponse yields null: the save was rejected and nothing persisted. */
export const DYNAMIC_SAVE_FAILED_MESSAGE =
  'Your form could not be saved. Your entries are still here, please try again. If it keeps failing, contact support.';

/** Form controls that render on a dynamic page but never carry an answer to save. */
const NON_RESPONSE_CONTROL_KEYS = new Set([
  'submit',
  'coaching',
  'ai-coaching',
  'review',
  'copyLinkButton',
  'last-modified',
]);

/** The form control keys whose values belong in the saved response. */
export function dynamicResponseFormProperties(
  controlKeys: Array<string>
): Array<string> {
  return controlKeys.filter((key) => !NON_RESPONSE_CONTROL_KEYS.has(key));
}

/**
 * True when activeAction's result means nothing was saved. The dynamic page resolves null
 * on a failed save (#34973); overrides resolve undefined or {} on success, so only null counts.
 */
export function dynamicSaveFailed(result: unknown): result is null {
  return result === null;
}

/**
 * True when activeAction's result saved only part of the form: a 200 whose rejectedControls names refused
 * answers (WI 34980). Callers keep the user on the form instead of closing or submitting (WI 34982).
 */
export function dynamicSavePartial(result: unknown): boolean {
  return !isWriteRefusedBody(result) && rejectedControlNames(result).length > 0;
}

/** True when the user must stay on the form: nothing saved (null) or part of it refused. Reviews submit only when false. */
export function dynamicSaveIncomplete(result: unknown): boolean {
  return dynamicSaveFailed(result) || dynamicSavePartial(result);
}

/** saveDynamicResponse's error result: the stage gate's 403 body, which names the refused controls (Task 34976), else null. */
export function dynamicSaveErrorResult(
  err: unknown
): IDynamicResponseBody | null {
  return isWriteRefusal(err)
    ? (err as { error: IDynamicResponseBody }).error
    : null;
}

/** Each refused control name as the label the form renders for it (accordion children included), else the name (WI 34980). */
export function dynamicRefusedControlLabels(
  names: Array<string>,
  controls?: Array<IDynamicModel> | null
): Array<string> {
  return names.map(
    (name) =>
      (controls?.length &&
        dynamicFindControlByCondition(
          controls,
          (c) => c.formControlName === name
        ).ctrl?.label) ||
      name
  );
}

/** How a save went: nothing persisted, persisted with some answers refused, or fully saved. */
export type DynamicSaveOutcome = 'unsaved' | 'partial' | 'saved';

/**
 * Alerts the user for a save that did not fully persist and reports how it went.
 * - 'unsaved': null (#34973, every other HTTP failure) or the gate's writeRefused 403 (Task 34976). The caller keeps
 *   the entries (no draft alert, no 3s clear, no formReviewDone$) and resolves null: null means nothing saved.
 * - 'partial': a 200 whose rejectedControls names refused answers; the rest saved, so show no success message and
 *   keep the user on the form (WI 34980).
 * Refused controls are named by their rendered label when `controls` is given.
 */
export function dynamicAlertSaveOutcome(
  res: IDynamicResponseBody | null,
  win: { alert(message: string): unknown },
  controls?: Array<IDynamicModel> | null
): DynamicSaveOutcome {
  const refused = dynamicRefusedControlLabels(
    rejectedControlNames(res),
    controls
  );
  if (!res || isWriteRefusedBody(res)) {
    void win.alert(
      res && refused.length
        ? writeRefusedMessage(refused)
        : DYNAMIC_SAVE_FAILED_MESSAGE
    );
    return 'unsaved';
  }
  if (refused.length) {
    void win.alert(writeRefusedMessage(refused));
    return 'partial';
  }
  return 'saved';
}
